/**
 * JavaScript personnalisé pour Nicha Paris
 * Optimisations UX et animations
 */

(function() {
  'use strict';

  if (window.NichaCustomInitialized) return;
  window.NichaCustomInitialized = true;

  // ============================================
  // ANIMATIONS AU SCROLL
  // ============================================
  
  const initScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('nicha-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.nicha-animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
  };

  // ============================================
  // CALCULATEUR DE DÉLAI PRÉ-COMMANDE
  // ============================================
  
  const initPreorderCalculator = () => {
    const preorderElements = document.querySelectorAll('[data-preorder-days]');
    
    preorderElements.forEach(element => {
      const days = parseInt(element.dataset.preorderDays, 10);
      if (isNaN(days)) return;
      
      const today = new Date();
      const deliveryDate = new Date(today);
      deliveryDate.setDate(today.getDate() + days);
      
      const options = { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      };
      const formattedDate = deliveryDate.toLocaleDateString('fr-FR', options);
      
      element.textContent = `Livraison prévue le ${formattedDate}`;
    });
  };

  // ============================================
  // LOUPE / MODAL IMAGE PRODUIT (fallback si le slider bloque les clics)
  // ============================================

  const initProductMediaModalFix = () => {
    if (document.body.dataset.nichaModalFix) return;
    document.body.dataset.nichaModalFix = '1';
    // Le slider bloque les clics : on écoute le modal-opener (zone image) pour ouvrir la modale
    document.addEventListener('click', (e) => {
      const opener = e.target.closest?.('modal-opener.product__modal-opener');
      if (!opener) return;
      const modalId = opener.getAttribute('data-modal');
      if (!modalId) return;
      const modal = document.querySelector(modalId);
      if (!modal || typeof modal.show !== 'function') return;
      e.preventDefault();
      e.stopPropagation();
      const toggle = opener.querySelector('.product__media-toggle');
      modal.show(toggle || opener);
    }, true);
  };

  // ============================================
  // GALERIE PRODUIT AMÉLIORÉE
  // ============================================
  
  const initProductGallery = () => {
    const gallery = document.querySelector('.nicha-product-gallery');
    if (!gallery) return;
    
    const mainImage = gallery.querySelector('.nicha-product-gallery__main img');
    const thumbnails = gallery.querySelectorAll('.nicha-product-gallery__thumb');
    
    if (!mainImage || thumbnails.length === 0) return;
    
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const newSrc = thumb.dataset.fullImage || thumb.href;
        
        mainImage.style.opacity = '0';
        setTimeout(() => {
          mainImage.src = newSrc;
          mainImage.style.opacity = '1';
        }, 150);
        
        thumbnails.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  };

  // ============================================
  // COMPTEUR DE STOCK/URGENCE
  // ============================================
  
  const initStockCounter = () => {
    const counters = document.querySelectorAll('[data-stock-count]');
    
    counters.forEach(counter => {
      const stock = parseInt(counter.dataset.stockCount, 10);
      if (isNaN(stock) || stock <= 0) return;
      
      if (stock <= 5) {
        counter.classList.add('low-stock');
        counter.textContent = `Plus que ${stock} en stock`;
      } else {
        counter.textContent = `${stock} en stock`;
      }
    });
  };

  // ============================================
  // SMOOTH SCROLL POUR ANCRES
  // ============================================
  
  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '#!') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  };

  // ============================================
  // LAZY LOADING DES IMAGES
  // ============================================
  
  const initLazyLoading = () => {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              img.classList.add('loaded');
            }
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  };

  // ============================================
  // OPTIMISATION MOBILE MENU
  // ============================================
  
  const initMobileMenu = () => {
    const menuToggle = document.querySelector('.nicha-mobile-menu-toggle');
    const menu = document.querySelector('.nicha-mobile-menu');
    
    if (!menuToggle || !menu) return;
    
    menuToggle.addEventListener('click', () => {
      menu.classList.toggle('active');
      document.body.classList.toggle('menu-open');
    });
    
    document.addEventListener('click', (e) => {
      if (!menu.contains(e.target) && !menuToggle.contains(e.target)) {
        menu.classList.remove('active');
        document.body.classList.remove('menu-open');
      }
    });
  };

  // ============================================
  // SMARTSIZE: BOUTON FACTICE ALIGNÉ AVEC "TAILLE"
  //
  // Principe :
  //   1. Le bloc Smartsize reste dans le DOM, on ne le déplace pas.
  //      On le cache avec display:none pour que ses gestionnaires
  //      de clic restent actifs.
  //   2. On ajoute un bouton factice « Guide des tailles » à droite
  //      du titre « Taille » dans la légende du fieldset.
  //   3. Au clic sur le bouton factice, on déclenche click() sur le
  //      vrai déclencheur Smartsize → le guide s'ouvre normalement.
  // ============================================

  const findSmartsizeTrigger = () => {
    const byId = document.querySelector('#opensize');
    if (byId) return byId;

    const byTrigger = document.querySelector('.smartsize-trigger, .smartsize-trigger--inline');
    if (byTrigger) return byTrigger;

    const byIcon = document.querySelector('.smartsize-button__icon');
    if (byIcon) return byIcon.closest('a, button, span, div') || byIcon;

    return Array.from(document.querySelectorAll('a, button, span, div'))
      .find((el) => (el.textContent || '').trim().toLowerCase() === 'guide des tailles') || null;
  };

  const hideSmartsizeBlock = () => {
    const container = document.querySelector('#sizefox-container');
    if (!container) return;

    // Masquer uniquement le bloc app Shopify (ancêtre sûr)
    // Ne jamais remonter au-delà sans classe explicite pour éviter de cacher les variantes
    const appBlock = container.closest('.shopify-app-block') ||
                     container.closest('.shopify-block');
    if (appBlock) {
      appBlock.style.setProperty('display', 'none', 'important');
    } else {
      // Pas de bloc app trouvé : masquer seulement le container lui-même
      container.style.setProperty('display', 'none', 'important');
    }

    document.body.classList.add('nicha-smartsize-fake-added');
  };

  const addSmartsizeFakeButton = (slot) => {
    if (!slot) return;
    hideSmartsizeBlock();
    if (slot.querySelector('.nicha-smartsize-fake-btn')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nicha-smartsize-fake-btn';
    btn.innerHTML = '<img class="smartsize-button__icon" src="https://cdn.shopify.com/s/files/1/0848/1321/8088/files/ruler.svg?v=1736774771" alt="" style="width:auto;height:1em;margin-right:0.5rem;vertical-align:middle;"><span>Guide des tailles</span>';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const t = findSmartsizeTrigger();
      if (t) t.click();
    });
    slot.appendChild(btn);
    document.body.classList.add('nicha-smartsize-fake-added');
  };

  const watchAndHideSmartsizeBlock = () => {
    // Masquer immédiatement si déjà présent
    hideSmartsizeBlock();

    // Observer pour le cacher dès qu'il apparaît dans le DOM
    if (document.body && document.body.dataset.sizefoxHideObserver !== 'true') {
      document.body.dataset.sizefoxHideObserver = 'true';
      const hideObserver = new MutationObserver(() => {
        if (document.querySelector('#sizefox-container')) {
          hideSmartsizeBlock();
        }
      });
      hideObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Réappliquer plusieurs fois pour couvrir le chargement tardif
    [200, 500, 1000, 2000, 3000].forEach((d) => setTimeout(hideSmartsizeBlock, d));
  };

  const moveSmartsizeIntoLegend = () => {
    // Ne pas afficher le bouton Guide des tailles sur la collection bijoux
    if (isBijouxCollection()) return false;

    hideSmartsizeBlock();

    const variantBlocks = Array.from(document.querySelectorAll('.product-form__input'));
    if (variantBlocks.length === 0) return false;

    const labelMatch = (el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      return text.includes('taille') || text.includes('size');
    };

    const targetBlock = variantBlocks.find((block) => {
      const label = block.querySelector('.form__label, legend.form__label');
      return label && labelMatch(label);
    }) || variantBlocks[0];

    if (!targetBlock) return false;
    targetBlock.classList.add('size-variant-block');

    const legendEl = targetBlock.querySelector('legend.form__label') || targetBlock.querySelector('legend');
    const labelEl = targetBlock.querySelector('.form__label');
    if (!legendEl && !labelEl) return false;

    if (legendEl) {
      legendEl.classList.add('sizefox-row', 'form__label');
      legendEl.style.cssText = 'display:flex;align-items:center;justify-content:space-between;width:100%;margin-bottom:0;padding-bottom:0;line-height:1;';

      let slot = legendEl.querySelector('.sizefox-slot');
      if (!slot) {
        slot = document.createElement('span');
        slot.className = 'sizefox-slot';
        slot.style.cssText = 'margin-left:auto;display:flex;align-items:center;flex-shrink:0;line-height:1;';
        legendEl.appendChild(slot);
      }
      addSmartsizeFakeButton(slot);

      // Réduire l'espace entre la legend et les boutons variantes.
      // La legend a 54px de hauteur (à cause du bouton) mais le fieldset n'a que 2px de padding-top.
      // L'espace visuel vient du padding-top (10px) des labels pill de Dawn.
      // On remonte le groupe de boutons en appliquant margin-top négatif sur le fieldset lui-même
      // MAIS seulement sur son padding interne via une translation de la legend vers le bas.
      // Solution retenue : margin-bottom négatif sur la legend pour "absorber" l'espace.
      if (!document.getElementById('nicha-pill-spacing')) {
        const s = document.createElement('style');
        s.id = 'nicha-pill-spacing';
        s.textContent = `
          fieldset.size-variant-block > legend {
            line-height: 1 !important;
          }
          fieldset.size-variant-block > legend .sizefox-slot {
            line-height: 1 !important;
          }
          fieldset.size-variant-block > legend .nicha-smartsize-fake-btn {
            line-height: 1 !important;
          }
        `;
        document.head.appendChild(s);
      }

      return true;
    }

    let row = targetBlock.querySelector('.sizefox-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'sizefox-row';
      row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;width:100%;';
      if (labelEl && labelEl.parentElement !== row) row.appendChild(labelEl);
      targetBlock.insertBefore(row, targetBlock.firstChild);
    }
    let slot = row.querySelector('.sizefox-slot');
    if (!slot) {
      slot = document.createElement('span');
      slot.className = 'sizefox-slot';
      slot.style.cssText = 'margin-left:auto;display:flex;align-items:center;flex-shrink:0;';
      row.appendChild(slot);
    }
    addSmartsizeFakeButton(slot);
    return true;
  };

  const initSmartsizePlacement = () => {
    if (moveSmartsizeIntoLegend()) return;

    if (document.body && document.body.dataset.sizefoxObserver !== 'true') {
      document.body.dataset.sizefoxObserver = 'true';
      const observer = new MutationObserver(() => {
        moveSmartsizeIntoLegend();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  const retrySmartsizePlacement = (attempts = 20) => {
    if (attempts <= 0) return;
    moveSmartsizeIntoLegend();
    setTimeout(() => retrySmartsizePlacement(attempts - 1), 500);
  };

  // ============================================
  // CHAMP PERSONNALISÉ ALTERNATIF (SHOPIFY)
  // ============================================

  const normalizeText = (value) => {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const isBijouxCollection = () => {
    const path = window.location.pathname || '';
    return /\/collections\/nos-bijoux(\/|$)/.test(path) || /\/products\/nos-bijoux(-|$)/.test(path);
  };

  const loadKlarnaScript = (callback) => {
    if (document.querySelector('script[src*="klarna"]') || window.Klarna) {
      if (callback) callback();
      return;
    }
    const clientId = window.NICHA_KLARNA_CLIENT_ID || '';
    if (!clientId) {
      console.warn('Nicha: Klarna non chargé. Définissez window.NICHA_KLARNA_CLIENT_ID avec votre Client ID Klarna (portail marchand).');
      if (callback) callback();
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
    script.dataset.environment = window.NICHA_KLARNA_ENV || 'production';
    script.dataset.clientId = clientId;
    script.onload = () => { if (callback) callback(); };
    script.onerror = () => { if (callback) callback(); };
    document.head.appendChild(script);
  };

  const getProductPriceCents = () => {
    const priceEl = document.querySelector('.price__regular .price-item, [data-product-price], .product__price .price, [data-price]');
    if (!priceEl) return '';
    const text = (priceEl.textContent || priceEl.dataset.price || '').replace(/[^\d,.]/g, '').replace(',', '.');
    const num = parseFloat(text);
    if (isNaN(num)) return '';
    return Math.round(num * 100).toString();
  };

  const initKlarnaPlacement = () => {
    const buttonsBlock = document.querySelector('.product-form__buttons');
    if (!buttonsBlock) return;

    const addToCartEl = buttonsBlock.querySelector('.product-form__submit, button[type="submit"]') ||
      Array.from(buttonsBlock.querySelectorAll('button, [type="submit"]')).find((el) =>
        /ajouter|add|panier|cart/i.test(el.textContent || '')
      );
    const insertBeforeEl = addToCartEl ? (addToCartEl.closest('.product-form__submit') || addToCartEl) : buttonsBlock.lastElementChild;
    if (!insertBeforeEl) return;

    let placement = document.querySelector('klarna-placement');
    if (!placement) {
      placement = document.createElement('klarna-placement');
      placement.setAttribute('data-key', 'credit-promotion-badge');
      placement.setAttribute('data-locale', 'fr-FR');
    }
    const amount = getProductPriceCents();
    placement.setAttribute('data-purchase-amount', amount || '');

    let wrapper = document.querySelector('.nicha-klarna-placement-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'nicha-klarna-placement-wrapper';
    }
    if (!wrapper.contains(placement)) wrapper.appendChild(placement);
    if (!buttonsBlock.contains(wrapper) || wrapper.nextElementSibling !== insertBeforeEl) {
      wrapper.remove();
      buttonsBlock.insertBefore(wrapper, insertBeforeEl);
    }
  };

  const loadCardoFont = () => {
    if (document.querySelector('link[href*="Cardo"]') || document.querySelector('link[href*="cardo"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cardo:ital,wght@0,400;0,700;1,400&display=swap';
    document.head.appendChild(link);
  };

  const injectKlarnaStyles = () => {
    loadCardoFont();
    if (document.getElementById('nicha-klarna-styles')) return;
    const style = document.createElement('style');
    style.id = 'nicha-klarna-styles';
    style.textContent = `
      .nicha-klarna-placement-wrapper {
        width: 100%;
      }
      klarna-placement {
        display: block;
        width: 100%;
        font-family: 'Cardo', Georgia, serif !important;
      }
      klarna-placement *,
      klarna-placement a,
      klarna-placement a:hover,
      klarna-placement a:visited,
      klarna-placement::part(osm-container),
      klarna-placement::part(osm-message) {
        font-family: 'Cardo', Georgia, serif !important;
      }
    `;
    document.head.appendChild(style);
  };

  const injectKlarnaShadowStyles = () => {
    document.querySelectorAll('klarna-placement').forEach((el) => {
      if (el.shadowRoot && !el.shadowRoot.querySelector('#nicha-klarna-shadow-styles')) {
        const style = document.createElement('style');
        style.id = 'nicha-klarna-shadow-styles';
        style.textContent = `
          *, *::before, *::after, a, a:hover, a:visited, span {
            font-family: 'Cardo', Georgia, serif !important;
          }
        `;
        el.shadowRoot.prepend(style);
      }
    });
  };

  const findSizeFieldset = () => {
    const blocks = Array.from(document.querySelectorAll('.product-form__input, fieldset, [class*="variant"], variant-radios'));
    const hasSizeRadios = (block) => block.querySelectorAll('input[type="radio"]').length > 0;
    const legendMatch = (el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      return text.includes('taille') || text.includes('size') || text.includes('mesures') || text.includes('measures');
    };
    const hasVosMesuresOption = (block) =>
      Array.from(block.querySelectorAll('label')).some((l) => {
        const n = normalizeText(l.textContent || '');
        return n.includes('vos mesures') || n.includes('your measures') || n.includes('made to your measures');
      });

    let found = blocks.find((block) => hasSizeRadios(block) && (() => {
      const label = block.querySelector('.form__label, legend, .form__label');
      return label && legendMatch(label);
    })());
    if (!found) {
      found = blocks.find((block) => hasSizeRadios(block) && hasVosMesuresOption(block));
    }
    return found || null;
  };

  const findCheckedSizeInput = () => {
    const sizeFieldset = findSizeFieldset();
    if (sizeFieldset) {
      const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked;
    }
    const all = Array.from(
      document.querySelectorAll('input[type="radio"][name*="Taille"]:checked, input[type="radio"][name*="Size"]:checked, input[type="radio"][name*="options"]:checked')
    );
    if (all.length > 0) {
      const visible = all.find((el) => el.offsetParent || el.getClientRects().length);
      return visible || all[0];
    }
    return document.querySelector('.product-form input[type="radio"]:checked');
  };

  const resolveFormFromInput = (inputEl) => {
    if (!inputEl) return null;
    if (inputEl.form) return inputEl.form;
    if (inputEl.getAttribute && inputEl.getAttribute('form')) {
      const formId = inputEl.getAttribute('form');
      return document.getElementById(formId);
    }
    return (inputEl.closest && inputEl.closest('form')) || null;
  };

  const findSizeContext = () => {
    const sizeFieldset = findSizeFieldset();
    if (sizeFieldset) {
      const form = sizeFieldset.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (form) return { form, anchorEl: sizeFieldset };
    }
    const forms = Array.from(
      document.querySelectorAll('.product-form form, form[action*="/cart/add"]')
    );
    if (forms.length === 0) return null;
    const labelMatch = (el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      return text.includes('taille') || text.includes('size');
    };

    for (const form of forms) {
      const checked =
        form.querySelector('input[type="radio"][name*="Taille"]:checked') ||
        form.querySelector('input[type="radio"][name*="Size"]:checked') ||
        form.querySelector('input[type="radio"]:checked');
      if (checked) {
        const anchorEl =
          checked.closest('.product-form__input') ||
          checked.closest('fieldset') ||
          checked.closest('.product-form') ||
          checked.parentElement;
        if (anchorEl) return { form, anchorEl };
      }

      const variantBlocks = Array.from(form.querySelectorAll('.product-form__input'));
      if (variantBlocks.length === 0) continue;
      const fieldset =
        variantBlocks.find((block) => {
          const label = block.querySelector('.form__label, legend.form__label, legend');
          return label && labelMatch(label);
        }) || null;
      if (fieldset) return { form, anchorEl: fieldset };
    }

    return null;
  };

  const getSelectedSizeValue = (containerEl) => {
    if (!containerEl) return '';
    const checked =
      containerEl.matches && containerEl.matches('input[type="radio"]')
        ? containerEl
        : containerEl.querySelector('input[type="radio"]:checked');
    if (checked) {
      const directValue = (checked.value || '').trim();
      if (directValue) return directValue;
      const labelRoot =
        (containerEl.closest && containerEl.closest('form')) ||
        (containerEl.closest && containerEl.closest('fieldset')) ||
        document;
      let labelText = '';
      if (checked.id) {
        const labelByFor = labelRoot.querySelector(`label[for="${checked.id}"]`);
        if (labelByFor && labelByFor.textContent) labelText = labelByFor.textContent.trim();
      }
      if (!labelText && checked.closest) {
        const wrappingLabel = checked.closest('label');
        if (wrappingLabel && wrappingLabel.textContent) labelText = wrappingLabel.textContent.trim();
      }
      return (
        labelText ||
        checked.dataset.value ||
        checked.getAttribute('data-value') ||
        checked.value ||
        ''
      ).trim();
    }
    const select =
      containerEl.matches && containerEl.matches('select')
        ? containerEl
        : containerEl.querySelector('select');
    if (select) {
      const option = select.selectedOptions && select.selectedOptions[0];
      return (
        (option && option.textContent) ||
        select.value ||
        ''
      ).trim();
    }
    return '';
  };

  const cleanupMeasurementFields = (targetForm) => {
    const all = Array.from(document.querySelectorAll('.nicha-measurement-field'));
    if (all.length === 0) return null;

    const formId = targetForm && targetForm.id ? targetForm.id : '';
    let kept = null;
    if (targetForm) {
      const matching = formId
        ? all.filter((node) => node.dataset.nichaFormId === formId)
        : [];
      const inForm = all.filter((node) => targetForm.contains(node));
      if (inForm.length > 0) {
        kept = inForm[0];
        inForm.slice(1).forEach((node) => node.remove());
      }
      if (!kept && matching.length > 0) {
        kept = matching[0];
        matching.slice(1).forEach((node) => node.remove());
      }
      all
        .filter((node) => !targetForm.contains(node))
        .filter((node) => {
          if (!formId) return true;
          return node.dataset.nichaFormId !== formId;
        })
        .forEach((node) => node.remove());
    } else {
      kept = all[0];
      all.slice(1).forEach((node) => node.remove());
    }

    return kept;
  };

  const resolveFormElement = (context, checkedInput) => {
    const formFromContext =
      context &&
      context.form &&
      context.form.tagName &&
      context.form.tagName.toLowerCase() === 'form'
        ? context.form
        : null;
    if (formFromContext) return formFromContext;
    if (checkedInput && checkedInput.form) return checkedInput.form;
    if (checkedInput && checkedInput.getAttribute) {
      const formId = checkedInput.getAttribute('form');
      if (formId) {
        const byId = document.getElementById(formId);
        if (byId) return byId;
      }
    }
    if (context && context.anchorEl && context.anchorEl.getAttribute) {
      const formId = context.anchorEl.getAttribute('form');
      if (formId) {
        const byId = document.getElementById(formId);
        if (byId) return byId;
      }
    }
    if (context && context.anchorEl && context.anchorEl.closest) {
      const closest = context.anchorEl.closest('form');
      if (closest) return closest;
    }
    return document.querySelector('form[action*="/cart/add"]');
  };

  const ensureFormId = (form) => {
    if (!form) return null;
    const currentId = String(form.id || '');
    if (!currentId || currentId.includes('[object')) {
      form.id = `nicha-product-form-${Date.now()}`;
    }
    return form.id;
  };

  const getOutermostDisabledFieldset = (node) => {
    if (!node || !node.closest) return null;
    const nearest = node.closest('fieldset[disabled]');
    if (!nearest) return null;
    let outermost = nearest;
    let parent = nearest.parentElement;
    while (parent) {
      if (
        parent.tagName &&
        parent.tagName.toLowerCase() === 'fieldset' &&
        parent.hasAttribute('disabled')
      ) {
        outermost = parent;
      }
      parent = parent.parentElement;
    }
    return outermost;
  };

  const forceTextareaEditable = (wrapper) => {
    if (!wrapper) return;
    const textarea = wrapper.querySelector('textarea');
    if (!textarea) return;
    textarea.disabled = false;
    textarea.readOnly = false;
    textarea.removeAttribute('disabled');
    textarea.removeAttribute('readonly');
    textarea.tabIndex = 0;
    textarea.style.pointerEvents = 'auto';
    textarea.style.userSelect = 'text';
    textarea.style.webkitUserSelect = 'text';
    textarea.style.position = 'relative';
    textarea.style.zIndex = '2';
    wrapper.style.pointerEvents = 'auto';
    wrapper.style.position = 'relative';
    wrapper.style.zIndex = '2';
  };

  const ensureCustomMeasurementField = (context) => {
    if (!context || !context.form) return null;
    const form = resolveFormElement(context, findCheckedSizeInput()) || context.form;
    const anchorEl = context.anchorEl || form;

    const formId = ensureFormId(form);

    let wrapper =
      cleanupMeasurementFields(form) ||
      document.querySelector(
        formId ? `.nicha-measurement-field[data-nicha-form-id="${formId}"]` : '.nicha-measurement-field'
      ) ||
      form.querySelector('.nicha-measurement-field');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'nicha-measurement-field';
      wrapper.setAttribute('data-nicha-measurement', 'true');
      if (formId) {
        wrapper.dataset.nichaFormId = String(formId);
      }

      const label = document.createElement('label');
      label.setAttribute('for', 'nicha-measurements');
      label.textContent = isBijouxCollection()
        ? 'Votre bijou est fabriqué uniquement pour vous dans notre studio de Chantilly.'
        : 'Votre vêtement-bijou est fabriqué uniquement pour vous dans notre atelier aux portes de Paris. Indiquez toute mesure que vous souhaiteriez personnaliser.';

      const textarea = document.createElement('textarea');
      textarea.id = 'nicha-measurements';
      textarea.name = 'properties[Mensurations]';
      textarea.rows = 4;
      textarea.placeholder = isBijouxCollection()
        ? 'Longueur, diamètre souhaité…'
        : 'Tour de taille, tour de poitrine, tour de hanches, longueur souhaitée...';
      if (formId) {
        textarea.setAttribute('form', String(formId));
      }

      wrapper.appendChild(label);
      wrapper.appendChild(textarea);

      if (anchorEl && anchorEl.parentNode) {
        const disabledFieldset = getOutermostDisabledFieldset(anchorEl);
        const variantRadios =
          anchorEl.closest && anchorEl.closest('variant-radios');
        const insertAfterEl = variantRadios || disabledFieldset || anchorEl;
        const insertParent = insertAfterEl.parentNode;
        if (insertAfterEl.nextSibling) {
          insertParent.insertBefore(wrapper, insertAfterEl.nextSibling);
        } else {
          insertParent.appendChild(wrapper);
        }
      } else {
        form.appendChild(wrapper);
      }
    }

    if (wrapper) {
      if (formId) {
        wrapper.dataset.nichaFormId = String(formId);
      }
      const textarea = wrapper.querySelector('textarea');
      if (textarea && formId && textarea.getAttribute('form') !== formId) {
        textarea.setAttribute('form', String(formId));
      }
    }

    if (wrapper && wrapper.closest && wrapper.closest('fieldset[disabled]')) {
      const disabledFieldset = getOutermostDisabledFieldset(wrapper);
      const insertAfterEl = form;
      const insertParent = form.parentNode || (disabledFieldset && disabledFieldset.parentNode);
      if (insertParent && insertAfterEl) {
        if (insertAfterEl.nextSibling) {
          insertParent.insertBefore(wrapper, insertAfterEl.nextSibling);
        } else {
          insertParent.appendChild(wrapper);
        }
      }
    }

    forceTextareaEditable(wrapper);
    return wrapper;
  };

  const getCheckedSizeValueFromRoot = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    const radios = Array.from(
      scope.querySelectorAll('input[type="radio"]')
    );
    const checked = radios.find((input) => input && input.checked);
    if (!checked) return '';
    const directValue = (checked.value || '').trim();
    if (directValue) return directValue;
    if (checked.id) {
      const label =
        (scope.querySelector && scope.querySelector(`label[for="${checked.id}"]`)) ||
        document.querySelector(`label[for="${checked.id}"]`);
      if (label && label.textContent) return label.textContent.trim();
    }
    if (checked.closest) {
      const wrappingLabel = checked.closest('label');
      if (wrappingLabel && wrappingLabel.textContent) return wrappingLabel.textContent.trim();
    }
    return '';
  };

  const getCheckedSizeValueFromVisibleVariant = () => {
    const sizeFieldset = findSizeFieldset();
    if (sizeFieldset) {
      const value = getCheckedSizeValueFromRoot(sizeFieldset);
      if (value) return value;
    }
    const variants = Array.from(document.querySelectorAll('variant-radios'));
    const labelMatch = (el) => {
      const t = (el.textContent || '').trim().toLowerCase();
      return t.includes('taille') || t.includes('size');
    };
    for (const variant of variants) {
      const isVisible = variant.offsetParent || variant.getClientRects().length;
      if (!isVisible) continue;
      const label = variant.querySelector('.form__label, legend');
      if (label && !labelMatch(label)) continue;
      const value = getCheckedSizeValueFromRoot(variant);
      if (value) return value;
    }
    return '';
  };

  const getCurrentSizeValueFromDOM = () => {
    const visibleValue = getCheckedSizeValueFromVisibleVariant();
    if (visibleValue) return visibleValue;
    const checkedInput = findCheckedSizeInput();
    if (checkedInput) {
      const container = checkedInput.closest('fieldset') || checkedInput.closest('.product-form__input') || checkedInput;
      return getSelectedSizeValue(container);
    }
    return '';
  };

  const isCustomSizeValue = (normalized) => {
    const n = (normalized || '').replace(/[-_\s]/g, '').toLowerCase();
    const s = (normalized || '').toLowerCase();
    return s.includes('a vos mesures') || s.includes('vos mesures') || s.includes('sur mesure') ||
      s.includes('made to your measures') || s.includes('your measures') ||
      n.includes('avosmesures') || n.includes('madetoyourmeasures') ||
      s.includes('custom');
  };

  const findAVosMesuresInput = () => {
    const search = (root) => {
      const labels = root.querySelectorAll ? root.querySelectorAll('label') : [];
      for (const label of labels) {
        const norm = normalizeText(label.textContent || '').trim();
        if (norm.length > 80) continue;
        const isVosMesures = norm.includes('a vos mesures') || norm.includes('avosmesures') || norm.includes('vos mesures') ||
          norm.includes('made to your measures') || norm.includes('your measures');
        if (!isVosMesures) continue;
        let input = null;
        const forId = label.getAttribute('for');
        if (forId) input = document.getElementById(forId);
        if (!input && label.querySelector) input = label.querySelector('input[type="radio"]');
        if (!input && label.parentElement) {
          const parent = label.parentElement;
          if (parent.querySelector) input = parent.querySelector('input[type="radio"]');
        }
        if (input && input.type === 'radio') return input;
      }
      const withShadow = root.querySelectorAll ? root.querySelectorAll('variant-radios, variant-selects, [class*="variant"]') : [];
      for (const el of withShadow) {
        if (el.shadowRoot) {
          const found = search(el.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    };
    return search(document.body);
  };

  const isAVosMesuresRadioChecked = () => {
    const input = findAVosMesuresInput();
    return input ? input.checked : false;
  };

  const bindAVosMesuresListeners = () => {
    const input = findAVosMesuresInput();
    if (!input || input.dataset.nichaBound === '1') return;
    input.dataset.nichaBound = '1';
    const handler = () => { updateCustomMeasurementField(); };
    input.addEventListener('change', handler);
    input.addEventListener('click', handler, true);
    const name = input.getAttribute('name');
    if (name) {
      const form = input.form || input.closest('form');
      const root = form || document;
      try {
        const siblings = root.querySelectorAll('input[type="radio"][name="' + CSS.escape(name) + '"]');
        siblings.forEach((r) => {
          if (r !== input) {
            r.addEventListener('change', handler);
            r.addEventListener('click', handler, true);
          }
        });
      } catch (_) {}
    }
  };

  const initMeasurementFieldNearAVosMesures = () => {
    const input = findAVosMesuresInput();
    if (!input) return null;
    let wrapper = document.querySelector('.nicha-measurement-field');
    if (wrapper) return wrapper;
    const form = input.form || input.closest('form');
    if (!form) return null;
    const insertPoint = input.closest('fieldset') || input.closest('.product-form__input') || input.closest('variant-radios') || input.closest('label')?.parentElement || input.parentElement;
    if (!insertPoint || !insertPoint.parentNode) return null;
    wrapper = document.createElement('div');
    wrapper.className = 'nicha-measurement-field';
    wrapper.style.display = 'none';
    const textarea = document.createElement('textarea');
    textarea.id = 'nicha-measurements';
    textarea.name = 'properties[Mensurations]';
    textarea.rows = 4;
    textarea.placeholder = isBijouxCollection()
      ? 'Longueur, diamètre souhaité…'
      : 'Tour de taille, tour de poitrine, tour de hanches, longueur souhaitée...';
    if (!form.id) form.id = 'nicha-product-form-' + Date.now();
    textarea.setAttribute('form', form.id);
    const label = document.createElement('label');
    label.setAttribute('for', 'nicha-measurements');
    label.textContent = isBijouxCollection()
      ? 'Votre bijou est fabriqué uniquement pour vous dans notre studio de Chantilly.'
      : 'Votre vêtement-bijou est fabriqué uniquement pour vous dans notre atelier aux portes de Paris. Indiquez toute mesure que vous souhaiteriez personnaliser.';
    wrapper.appendChild(label);
    wrapper.appendChild(textarea);
    insertPoint.parentNode.insertBefore(wrapper, insertPoint.nextSibling);
    const sync = () => {
      const show = input.checked;
      wrapper.style.display = show ? '' : 'none';
      wrapper.hidden = !show;
      if (textarea) {
        textarea.disabled = !show;
        textarea.readOnly = false;
      }
    };
    sync();
    input.addEventListener('change', sync);
    input.addEventListener('click', () => setTimeout(sync, 50));
    const name = input.getAttribute('name');
    if (name) {
      try {
        const root = form || document;
        root.querySelectorAll('input[type="radio"][name="' + CSS.escape(name) + '"]').forEach((r) => {
          r.addEventListener('change', sync);
          r.addEventListener('click', () => setTimeout(sync, 50));
        });
      } catch (_) {}
    }
    return wrapper;
  };

  const updateCustomMeasurementField = () => {
    const directWrapper = initMeasurementFieldNearAVosMesures();
    if (directWrapper) {
      const input = findAVosMesuresInput();
      if (input) {
        directWrapper.style.display = input.checked ? '' : 'none';
        directWrapper.hidden = !input.checked;
      }
      return;
    }
    let context = null;
    const checkedInput = findCheckedSizeInput();
    if (checkedInput) {
      context = {
        form: resolveFormFromInput(checkedInput),
        anchorEl: checkedInput.closest('.product-form__input') || checkedInput.closest('fieldset') || checkedInput.closest('.product-form') || checkedInput.parentElement
      };
    }
    if (!context) context = findSizeContext();
    if (!context) {
      const avosInput = findAVosMesuresInput();
      if (avosInput) {
        const form = avosInput.form || avosInput.closest('form') || document.querySelector('form[action*="/cart/add"]');
        const anchorEl = avosInput.closest('.product-form__input') || avosInput.closest('fieldset') || avosInput.closest('variant-radios') || avosInput.parentElement;
        if (form && anchorEl) context = { form, anchorEl };
      }
    }
    if (!context) {
      const form = document.querySelector('.product-form form, form[action*="/cart/add"]');
      if (form) context = { form, anchorEl: form };
    }
    if (!context || !context.form) {
      cleanupMeasurementFields(null);
      return;
    }
    const currentValueFromDOM = getCurrentSizeValueFromDOM();
    const normalizedCurrent = normalizeText(currentValueFromDOM);
    if (normalizedCurrent) {
      window.NichaLastSizeValue = normalizedCurrent;
    }
    const directCheck = isAVosMesuresRadioChecked();
    const effectiveValue = (normalizedCurrent || window.NichaLastSizeValue || '');
    const shouldShow = directCheck || isCustomSizeValue(effectiveValue);
    const wrapper = ensureCustomMeasurementField(context);
    if (!wrapper) return;
    wrapper.style.display = shouldShow ? '' : 'none';
    wrapper.hidden = !shouldShow;
    wrapper.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    forceTextareaEditable(wrapper);
  };

  const initCustomMeasurementField = () => {
    if (window.NichaCustomMeasurementInitialized) return;
    window.NichaCustomMeasurementInitialized = true;

    const run = () => {
      bindAVosMesuresListeners();
      updateCustomMeasurementField();
    };
    [0, 100, 300, 600, 1000, 1500, 2500, 4000].forEach((delay) => {
      setTimeout(run, delay);
    });
    run();

    const isInSizeFieldset = (el) => {
      const fieldset = findSizeFieldset();
      return fieldset && fieldset.contains && fieldset.contains(el);
    };

    const setLastSizeFromInputOrLabel = (input, labelEl) => {
      if (input && input.matches && input.matches('input[type="radio"]')) {
        if (isInSizeFieldset(input)) {
          const container = input.closest('fieldset') || input.closest('.product-form__input') || input;
          window.NichaLastSizeValue = normalizeText(getSelectedSizeValue(container));
        }
      } else if (labelEl && isInSizeFieldset(labelEl)) {
        window.NichaLastSizeValue = normalizeText(labelEl.textContent || '');
      }
      const v = (window.NichaLastSizeValue || '').toLowerCase();
      window.NichaLastSizeIsCustom =
        v.includes('a vos mesures') || v.includes('vos mesures') || v.includes('made to your measures') || v.includes('your measures');
    };

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.matches && target.matches('input[type="radio"], select')) {
        if (target.matches('input[type="radio"]') && isInSizeFieldset(target)) {
          setLastSizeFromInputOrLabel(target, null);
        }
        updateCustomMeasurementField();
      }
    });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target) return;
      const label = target.closest ? target.closest('label') : null;
      const labelNorm = label ? normalizeText(label.textContent || '') : '';
      if (label && label.textContent.length < 80 && (labelNorm.includes('vos mesures') || labelNorm.includes('your measures') || labelNorm.includes('made to your measures'))) {
        setTimeout(() => updateCustomMeasurementField(), 10);
      }
      let input = null;
      let labelEl = null;
      if (target.matches && target.matches('input[type="radio"]') && isInSizeFieldset(target)) {
        input = target;
      } else if (label && label.getAttribute('for')) {
        const inputId = label.getAttribute('for');
        input = inputId ? document.getElementById(inputId) : null;
        if (input && isInSizeFieldset(input)) labelEl = label;
      }
      if (input || labelEl) {
        setLastSizeFromInputOrLabel(input, labelEl);
        updateCustomMeasurementField();
      }
    }, true);

    document.addEventListener('input', (event) => {
      if (event.target && event.target.matches('input[type="radio"]') && isInSizeFieldset(event.target)) {
        setLastSizeFromInputOrLabel(event.target, null);
        updateCustomMeasurementField();
      }
    }, true);

    document.addEventListener('variant:change', () => {
      updateCustomMeasurementField();
      setTimeout(() => loadKlarnaScript(() => { initKlarnaPlacement(); injectKlarnaShadowStyles(); }), 100);
    });

    if (document.body && document.body.dataset.nichaMeasurementObserver !== 'true') {
      document.body.dataset.nichaMeasurementObserver = 'true';
      let updateScheduled = null;
      const observer = new MutationObserver(() => {
        if (updateScheduled) return;
        updateScheduled = setTimeout(() => {
          updateScheduled = null;
          bindAVosMesuresListeners();
          updateCustomMeasurementField();
          injectKlarnaStyles();
          loadKlarnaScript(() => { initKlarnaPlacement(); injectKlarnaShadowStyles(); });
        }, 50);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  // ============================================
  // INTERCEPTION AJOUT AU PANIER - PROPRIÉTÉS
  // ============================================

  const initCartAddInterceptor = () => {
    if (window.NichaCartInterceptorActive) return;
    window.NichaCartInterceptorActive = true;
    const origFetch = window.fetch;
    window.fetch = function(url, options) {
      const urlStr = typeof url === 'string' ? url : (url && url.url) || '';
      if (urlStr.includes('/cart/add')) {
        const textarea = document.getElementById('nicha-measurements');
        if (textarea && textarea.value.trim() && !textarea.disabled) {
          const val = textarea.value.trim();
          if (options && options.body instanceof FormData) {
            options.body.set('properties[Mensurations]', val);
          } else if (options && options.body && typeof options.body.append === 'function') {
            options.body.append('properties[Mensurations]', val);
          } else if (options && options.body && typeof options.body === 'string') {
            try {
              const data = JSON.parse(options.body);
              if (!data.properties) data.properties = {};
              data.properties.Mensurations = val;
              options = { ...options, body: JSON.stringify(data) };
            } catch (_) {}
          }
        }
      }
      return origFetch.apply(this, arguments);
    };
  };

  // ============================================
  // AGrandissement des polices sur pages collections
  // ============================================
  
  const injectCollectionFontStyles = () => {
    if (document.getElementById('nicha-collection-fonts-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'nicha-collection-fonts-styles';
    style.textContent = `
      /* Styles pour les titres de produits sur pages collections */
      body.template-collection .card__heading,
      body.template-collection .card__heading a,
      body.template-collection .card__heading h2,
      body.template-collection .card__heading h3,
      body.template-collection .card__information .card__heading,
      body.template-collection .card__information .card__heading a,
      body.template-collection .card-wrapper .card__heading,
      body.template-collection .card-wrapper .card__heading a,
      body[class*="collection"] .card__heading,
      body[class*="collection"] .card__heading a,
      body[class*="collection"] .card__information .card__heading,
      body[class*="collection"] .card__information .card__heading a,
      .collection .card__heading,
      .collection .card__heading a,
      .collection .card__information .card__heading,
      .collection .card__information .card__heading a,
      .grid__item .card__heading,
      .grid__item .card__heading a,
      .grid__item .card__information .card__heading,
      .grid__item .card__information .card__heading a {
        font-size: 1.75rem !important;
        font-weight: normal !important;
        line-height: 1.3 !important;
      }
      
      /* Styles pour les prix sur pages collections */
      body.template-collection .price,
      body.template-collection .price__regular,
      body.template-collection .price-item,
      body.template-collection .price-item--regular,
      body.template-collection .card__information .price,
      body.template-collection .card__information .price__regular,
      body.template-collection .card__information .price-item,
      body.template-collection .card-wrapper .price,
      body.template-collection .card-wrapper .price__regular,
      body[class*="collection"] .price,
      body[class*="collection"] .price__regular,
      body[class*="collection"] .price-item,
      body[class*="collection"] .card__information .price,
      body[class*="collection"] .card__information .price__regular,
      .collection .price,
      .collection .price__regular,
      .collection .price-item,
      .collection .card__information .price,
      .collection .card__information .price__regular,
      .grid__item .price,
      .grid__item .price__regular,
      .grid__item .price-item,
      .grid__item .card__information .price,
      .grid__item .card__information .price__regular {
        font-size: 1.75rem !important;
        font-weight: normal !important;
      }
      
      /* Styles pour les vendors (PRÊT À PARTIR) sur pages collections */
      body.template-collection .card__vendor,
      body.template-collection .card__information .card__vendor,
      body.template-collection .card-wrapper .card__vendor,
      body.template-collection .card__caption .card__vendor,
      body.template-collection [class*="vendor"],
      body.template-collection .card__information [class*="vendor"],
      body[class*="collection"] .card__vendor,
      body[class*="collection"] .card__information .card__vendor,
      body[class*="collection"] [class*="vendor"],
      .collection .card__vendor,
      .collection .card__information .card__vendor,
      .collection [class*="vendor"],
      .grid__item .card__vendor,
      .grid__item .card__information .card__vendor,
      .grid__item [class*="vendor"],
      .card__vendor,
      .card__information .card__vendor,
      [class*="card"][class*="vendor"] {
        font-size: 1.25rem !important;
        font-weight: normal !important;
        letter-spacing: 0.05em !important;
      }
      
      @media (max-width: 768px) {
        body.template-collection .card__heading,
        body.template-collection .card__heading a,
        body.template-collection .card__information .card__heading,
        body[class*="collection"] .card__heading,
        body[class*="collection"] .card__heading a,
        .collection .card__heading,
        .collection .card__heading a,
        .grid__item .card__heading,
        .grid__item .card__heading a {
          font-size: 1.5rem !important;
          font-weight: normal !important;
        }
        
        body.template-collection .price,
        body.template-collection .price__regular,
        body.template-collection .price-item,
        body[class*="collection"] .price,
        body[class*="collection"] .price__regular,
        .collection .price,
        .collection .price__regular,
        .grid__item .price,
        .grid__item .price__regular {
          font-size: 1.5rem !important;
          font-weight: normal !important;
        }
        
        body.template-collection .card__vendor,
        body.template-collection .card__information .card__vendor,
        body.template-collection [class*="vendor"],
        body[class*="collection"] .card__vendor,
        body[class*="collection"] .card__information .card__vendor,
        body[class*="collection"] [class*="vendor"],
        .collection .card__vendor,
        .collection .card__information .card__vendor,
        .collection [class*="vendor"],
        .grid__item .card__vendor,
        .grid__item .card__information .card__vendor,
        .grid__item [class*="vendor"],
        .card__vendor,
        .card__information .card__vendor {
          font-size: 1.125rem !important;
          font-weight: normal !important;
        }
      }
    `;
    document.head.appendChild(style);
  };
  
  const enlargeCollectionFonts = () => {
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                            window.location.pathname.includes('/collections/') ||
                            document.querySelector('.collection') ||
                            document.querySelector('[class*="collection"]');
    
    if (!isCollectionPage) return;
    
    const titleSelectors = [
      '.card__heading',
      '.card__heading a',
      '.card__heading h2',
      '.card__heading h3',
      '.card__information .card__heading',
      '.card__information .card__heading a',
      '.card-information .card__heading',
      '.card-wrapper .card__heading',
      '.grid__item .card__heading',
      '.product-card__title',
      '.product-card .card__heading'
    ];
    
    titleSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.style.fontSize = '1.75rem';
        el.style.fontWeight = 'normal';
        el.style.lineHeight = '1.3';
      });
    });
    
    const priceSelectors = [
      '.card__information .price',
      '.card__information .price__regular',
      '.card__information .price-item',
      '.card__information .price-item--regular',
      '.card-information .price',
      '.card-wrapper .price',
      '.card-wrapper .price__regular',
      '.grid__item .price',
      '.grid__item .price__regular',
      '.grid__item .price-item',
      '.product-card__price',
      '.product-card .price'
    ];
    
    priceSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.style.fontSize = '1.75rem';
        el.style.fontWeight = 'normal';
      });
    });
    
    const vendorSelectors = [
      '.card__vendor',
      '.card__information .card__vendor',
      '.card-information .card__vendor',
      '.card-wrapper .card__vendor',
      '.card__caption .card__vendor',
      '.grid__item .card__vendor',
      '.grid__item .card__information .card__vendor',
      '.product-card__vendor',
      '.product-card .card__vendor',
      '[class*="vendor"]'
    ];
    
    vendorSelectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(el => {
          const text = (el.textContent || '').trim().toUpperCase();
          const isInCard = el.closest('.card') || el.closest('.grid__item') || el.closest('[class*="card"]');
          
          if (text.includes('PRÊT À PARTIR') || text.includes('PRET A PARTIR') || isInCard) {
            el.style.fontSize = '1.25rem';
            el.style.fontWeight = 'normal';
            el.style.letterSpacing = '0.05em';
          }
        });
      } catch (e) {}
    });
    
    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      const text = (el.textContent || '').trim().toUpperCase();
      if ((text === 'PRÊT À PARTIR' || text === 'PRET A PARTIR') && 
          (el.closest('.card') || el.closest('.grid__item') || el.closest('[class*="collection"]'))) {
        el.style.fontSize = '1.25rem';
        el.style.fontWeight = 'normal';
        el.style.letterSpacing = '0.05em';
      }
    });
    
    if (window.innerWidth <= 768) {
      titleSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.style.fontSize = '1.5rem';
          el.style.fontWeight = 'normal';
        });
      });
      
      priceSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.style.fontSize = '1.5rem';
          el.style.fontWeight = 'normal';
        });
      });
      
      vendorSelectors.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => {
            const text = (el.textContent || '').trim().toUpperCase();
            const isInCard = el.closest('.card') || el.closest('.grid__item') || el.closest('[class*="card"]');
            
            if (text.includes('PRÊT À PARTIR') || text.includes('PRET A PARTIR') || isInCard) {
              el.style.fontSize = '1.125rem';
              el.style.fontWeight = 'normal';
            }
          });
        } catch (e) {}
      });
      
      const allElementsMobile = document.querySelectorAll('*');
      allElementsMobile.forEach(el => {
        const text = (el.textContent || '').trim().toUpperCase();
        if ((text === 'PRÊT À PARTIR' || text === 'PRET A PARTIR') && 
            (el.closest('.card') || el.closest('.grid__item') || el.closest('[class*="collection"]'))) {
          el.style.fontSize = '1.125rem';
          el.style.fontWeight = 'normal';
        }
      });
    }
  };

  // ============================================
  // Section multicolonnes (lithothérapie) : éviter chevauchement du slider produit
  // ============================================
  const initMulticolumnOverlapFix = () => {
    if (!document.querySelector('.product')) return;
    const mc = document.querySelector('.multicolumn.color-background-1');
    if (mc) {
      if (!mc.dataset.nichaOverlapFixed) {
        mc.dataset.nichaOverlapFixed = '1';
        mc.style.position = 'relative';
        mc.style.zIndex = '5';
        mc.style.marginTop = '5rem';
        mc.style.paddingTop = '2rem';
      }
      if (window.matchMedia('(max-width: 989px)').matches) {
        const pageWidth = mc.querySelector('.page-width');
        if (pageWidth) {
          pageWidth.style.setProperty('padding-left', '1.5rem', 'important');
          pageWidth.style.setProperty('padding-inline-start', '1.5rem', 'important');
        }
      }
      document.body.classList.add('nicha-product-bijoux');
    } else {
      document.body.classList.remove('nicha-product-bijoux');
    }
  };

  // Réduire gap image principale ↔ slider
  const applyProductMediaGapFix = () => {
    if (!document.querySelector('.product')) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    const set = (el, p, v) => { if (el) el.style.setProperty(p, v, 'important'); };
    const gallery = document.querySelector('media-gallery.product__media-gallery, .product__media-gallery');
    if (gallery) {
      set(gallery, 'display', 'flex');
      set(gallery, 'flex-direction', 'column');
      set(gallery, 'gap', '0.25rem');
      set(gallery, 'row-gap', '0.25rem');
      const searchRoot = gallery.shadowRoot || gallery;
      [].forEach.call(searchRoot.children, (child) => {
        set(child, 'flex', '0 1 auto');
        set(child, 'margin-top', '0');
        set(child, 'margin-bottom', '0');
      });
      if (searchRoot.querySelectorAll) {
        searchRoot.querySelectorAll('.product__media-list, .thumbnail-slider').forEach((el) => {
          set(el, 'flex', '0 1 auto');
          set(el, 'margin-top', '0');
          set(el, 'margin-bottom', '0');
        });
      }
    }
    document.querySelectorAll('.product__media-list, ul.product__media-list').forEach((el) => {
      set(el, 'flex', '0 1 auto');
      set(el, 'margin-bottom', '0');
    });
    document.querySelectorAll('.thumbnail-slider').forEach((el) => set(el, 'margin-top', '0'));
  };

  // ESPACE IMAGE ↔ TITRE (mobile) : styles inline pour priorité maximale
  // ============================================
  const applyProductMobileImageTitleSpacing = () => {
    if (!document.querySelector('.product') || !window.matchMedia('(max-width: 989px)').matches) return;
    const mediaEl = document.querySelector('.product__media-wrapper, .product__media-section, .product__media-gallery');
    const infoEl = document.querySelector('.product__info-wrapper, .product__info-section');
    const productEl = document.querySelector('.product, .product__inner, [id*="MainProduct"] .product');
    if (mediaEl) {
      mediaEl.style.marginBottom = '0';
      mediaEl.style.paddingBottom = '0';
    }
    if (infoEl) {
      infoEl.style.marginTop = '0.75rem';
      infoEl.style.paddingTop = '0';
    }
    if (productEl) {
      productEl.style.gap = '0';
      productEl.style.rowGap = '0';
    }
  };

  const initProductMobileImageTitleSpacing = () => {
    if (!document.querySelector('.product') || document.getElementById('nicha-product-mobile-spacing')) return;
    applyProductMobileImageTitleSpacing();
    window.addEventListener('resize', applyProductMobileImageTitleSpacing);
    const style = document.createElement('style');
    style.id = 'nicha-product-mobile-spacing';
    style.textContent = `
      @media (max-width: 989px) {
        body.template-product .product,
        body.template-product .product .grid,
        body.template-product .product__inner,
        body.template-product .product__container,
        body.template-product [id*="MainProduct"] .grid,
        body.template-product [id*="MainProduct"] .product,
        body.template-product section[id*="main-product"] .grid,
        body.template-product section .product.grid {
          gap: 0 !important;
          row-gap: 0 !important;
          --grid-mobile-vertical-spacing: 0 !important;
        }
        body.template-product .grid__item.product__media-wrapper,
        body.template-product .product__media-wrapper,
        body.template-product .product__media-gallery,
        body.template-product media-gallery,
        body.template-product .slider-mobile-gutter,
        body.template-product slider-component.slider-mobile-gutter,
        body.template-product .product__media-list,
        body.template-product ul.product__media-list,
        body.template-product .product__media-list.grid--peek,
        body.template-product .product__media-section {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        body.template-product .product__media-list,
        body.template-product ul.product__media-list {
          padding-bottom: 0 !important;
        }
        body.template-product .grid__item.product__info-wrapper,
        body.template-product .product__info-wrapper,
        body.template-product .product__info-section {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        body.template-product .product__info-wrapper {
          margin-top: 0.75rem !important;
        }
      }
    `;
    document.head.appendChild(style);
  };

  // TITRE + PARTAGER : même ligne (mobile) - wrap en flex via JS
  // ============================================
  const initProductTitleShareRow = () => {
    if (!document.querySelector('.product') || !window.matchMedia('(max-width: 989px)').matches) return;
    if (document.getElementById('nicha-title-share-row')) return;
    const container = document.querySelector('.product__info-container');
    const shareBtn = document.querySelector('.share-button');
    const titleEl = document.querySelector('.product__info-container .product__title, .product__info-container h1');
    if (!container || !shareBtn || !titleEl) return;
    const wrapper = document.createElement('div');
    wrapper.id = 'nicha-title-share-row';
    wrapper.style.cssText = 'display:flex!important;align-items:center!important;justify-content:space-between!important;width:100%!important;flex-wrap:wrap!important;gap:.5rem!important;margin-bottom:0!important;';
    container.insertBefore(wrapper, titleEl);
    wrapper.appendChild(titleEl);
    wrapper.appendChild(shareBtn);
    titleEl.style.marginBottom = '0';
  };

  // ESPACE TITRE / PRÊT À PARTIR (mobile uniquement)
  // ============================================

  // ESPACE BLOCS PRODUIT (prix, variantes, globo, klarna, etc.) : styles inline
  // ============================================
  const applyProductInfoBlockSpacing = () => {
    if (!document.querySelector('.product') || !window.matchMedia('(max-width: 989px)').matches) return;
    const container = document.querySelector('.product__info-container');
    const productSection = document.querySelector('.product, [id*="MainProduct"]');
    if (!container) return;
    const setStyle = (el, prop, val, imp) => { if (el) el.style.setProperty(prop, val, imp ? 'important' : ''); };
    setStyle(container, 'display', 'flex', true);
    setStyle(container, 'flex-wrap', 'wrap', true);
    setStyle(container, 'align-items', 'flex-start', true);
    setStyle(container, 'gap', '0.5rem', true);
    [].forEach.call(container.children, (child) => {
      setStyle(child, 'margin-top', '0', true);
      setStyle(child, 'margin-bottom', '0', true);
    });
    container.querySelectorAll('.product__price').forEach(el => setStyle(el, 'margin-bottom', '0', true));
    container.querySelectorAll('.product-form, .product-form__buttons').forEach(el => {
      setStyle(el, 'margin-top', '0', true); setStyle(el, 'margin-bottom', '0', true);
    });
    container.querySelectorAll('.product-form__input').forEach(el => {
      setStyle(el, 'margin-top', '0', true); setStyle(el, 'margin-bottom', '0', true);
    });
    (productSection || document).querySelectorAll('.globo-product-options, [class*="globo"], [data-globo-product-options]').forEach(el => {
      setStyle(el, 'margin-top', '-1rem', true); setStyle(el, 'margin-bottom', '0', true);
    });
    (productSection || document).querySelectorAll('.nicha-klarna-placement-wrapper').forEach(el => {
      setStyle(el, 'margin-top', '-1rem', true); setStyle(el, 'margin-bottom', '1.25rem', true);
    });
    (productSection || document).querySelectorAll('klarna-placement').forEach(el => {
      setStyle(el, 'margin-top', '0', true); setStyle(el, 'margin-bottom', '0', true);
    });
    (productSection || document).querySelectorAll('[class*="trackingmore"], [class*="edd"]').forEach(el => {
      setStyle(el, 'margin-top', '0', true); setStyle(el, 'margin-bottom', '0', true);
    });
    (productSection || document).querySelectorAll('.shopify-block, [class*="shopify-app-block"]').forEach((block) => {
      if (block.querySelector && block.querySelector('[class*="trackingmore"], [class*="edd"]')) {
        setStyle(block, 'margin-bottom', '1.25rem', true);
      }
    });
  };

  const initProductVendorSpacing = () => {
    if (!document.querySelector('.product') || !window.matchMedia('(max-width: 989px)').matches) return;
    let el = document.querySelector('.product__vendor, .product__info-wrapper .product__vendor');
    if (el) {
      el.style.marginTop = '0.75rem';
      return;
    }
    const info = document.querySelector('.product__info-wrapper, [id*="ProductInfo"]');
    if (!info) return;
    for (const node of info.querySelectorAll('p, div, span, h2, h3')) {
      const t = (node.textContent || '').trim().toUpperCase().replace(/\s+/g, ' ');
      if (t === 'PRÊT À PARTIR' || t === 'PRET A PARTIR') {
        node.style.marginTop = '0.75rem';
        break;
      }
    }
  };

  // ============================================
  // INITIALISATION
  // ============================================
  
  const init = () => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    initCartAddInterceptor();
    initScrollAnimations();
    initPreorderCalculator();
    initProductGallery();
    initProductMediaModalFix();
    [100, 500, 1500].forEach((d) => setTimeout(initProductMediaModalFix, d));
    initProductMobileImageTitleSpacing();
    [100, 400, 1000].forEach((d) => setTimeout(() => { if (typeof applyProductMobileImageTitleSpacing === 'function') applyProductMobileImageTitleSpacing(); }, d));
    applyProductInfoBlockSpacing();
    [200, 600, 1200, 2500].forEach((d) => setTimeout(applyProductInfoBlockSpacing, d));
    window.addEventListener('resize', () => setTimeout(applyProductInfoBlockSpacing, 100));
    const productInfo = document.querySelector('.product__info-wrapper, [id*="ProductInfo"]');
    if (productInfo && typeof MutationObserver !== 'undefined') {
      let debounce;
      const mo = new MutationObserver(() => {
        clearTimeout(debounce);
        debounce = setTimeout(applyProductInfoBlockSpacing, 150);
      });
      mo.observe(productInfo, { childList: true, subtree: true });
    }
    initProductTitleShareRow();
    [100, 400].forEach((d) => setTimeout(initProductTitleShareRow, d));
    initProductVendorSpacing();
    [200, 600].forEach((d) => setTimeout(initProductVendorSpacing, d));
    initMulticolumnOverlapFix();
    applyProductMediaGapFix();
    [300, 800, 1500].forEach((d) => setTimeout(() => { initMulticolumnOverlapFix(); applyProductMediaGapFix(); }, d));
    window.addEventListener('resize', () => { setTimeout(initMulticolumnOverlapFix, 50); setTimeout(applyProductMediaGapFix, 100); });
    window.addEventListener('resize', () => setTimeout(initProductVendorSpacing, 100));
    initStockCounter();
    initSmoothScroll();
    initLazyLoading();
    initMobileMenu();
    watchAndHideSmartsizeBlock();
    initSmartsizePlacement();
    retrySmartsizePlacement();
    initCustomMeasurementField();
    injectKlarnaStyles();
    injectCollectionFontStyles();
    enlargeCollectionFonts();
    loadKlarnaScript(() => {
      initKlarnaPlacement();
      injectKlarnaShadowStyles();
      [300, 800, 1500].forEach((d) => setTimeout(injectKlarnaShadowStyles, d));
    });
    
    setTimeout(enlargeCollectionFonts, 500);
    setTimeout(enlargeCollectionFonts, 1500);
    setTimeout(enlargeCollectionFonts, 3000);
    
    if (document.body && document.body.dataset.nichaCollectionFontObserver !== 'true') {
      document.body.dataset.nichaCollectionFontObserver = 'true';
      const fontObserver = new MutationObserver(() => {
        enlargeCollectionFonts();
      });
      fontObserver.observe(document.body, { childList: true, subtree: true });
      
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(enlargeCollectionFonts, 250);
      });
    }
  };

  init();

  // ============================================
  // FORCER LES STYLES POUR VTLS TEXTBOX (PAGE ATELIER-BOUTIQUE)
  // ============================================
  
  const applyVtlsTextBoxStyles = () => {
    const descriptions = document.querySelectorAll('.VtlsTextBox__Description');
    
    descriptions.forEach(desc => {
      // Forcer la justification du texte
      desc.style.textAlign = 'justify';
      
      // Forcer le padding-right
      const isMobile = window.innerWidth <= 768;
      desc.style.paddingRight = isMobile ? '1rem' : '2rem';
      desc.style.marginRight = '0';
      
      // Justifier tous les éléments enfants
      const textElements = desc.querySelectorAll('p, li, div, span, h1, h2, h3, h4, h5, h6');
      textElements.forEach(el => {
        el.style.textAlign = 'justify';
      });
    });
  };
  
  // Appliquer immédiatement et après le chargement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applyVtlsTextBoxStyles();
      // Réappliquer après un court délai pour les apps qui chargent en différé
      setTimeout(applyVtlsTextBoxStyles, 500);
      setTimeout(applyVtlsTextBoxStyles, 1500);
    });
  } else {
    applyVtlsTextBoxStyles();
    setTimeout(applyVtlsTextBoxStyles, 500);
    setTimeout(applyVtlsTextBoxStyles, 1500);
  }
  
  // Réappliquer lors du redimensionnement
  window.addEventListener('resize', applyVtlsTextBoxStyles);
  
  // Réappliquer lors du rechargement de sections Shopify
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', applyVtlsTextBoxStyles);
    document.addEventListener('shopify:section:reorder', applyVtlsTextBoxStyles);
  }

  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:reorder', init);
  }

})();
